import { hexToRgbA } from '@/helper/Color';
import { useThemeColor } from '@/hooks/useThemeColor';
import { useCallback, useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';

const normalizeStyle = (value) => {
    if (Array.isArray(value)) return StyleSheet.flatten(value) || {};
    if (value && typeof value === 'object') return StyleSheet.flatten([value]) || {};
    return {};
};

export const usePressableMotion = (options) => {

    const {
        style = {},
        hoverStyle = null,
        onPressIn = null,
        onPressOut = null,
        onHoverIn = null,
        onHoverOut = null,
        light = null,
        dark = null,
        noSwitch = false,
        reduceMotion = false,
        inverted = false,
        disabled = false,
        type = 'default',
        variant = null,
    } = options;

    const usedColor = inverted ? { light: dark, dark: light } : { light, dark };

    const backgroundColor = useThemeColor(usedColor, 'background');

    const [localStyle, setLocalStyle] = useState(normalizeStyle(style));

    const updateStyles = useCallback((attr, ignoreCurrentBG = false) => {
        let append = {};

        if (!noSwitch && !ignoreCurrentBG) {
            append.backgroundColor = backgroundColor;
        }

        if (type === 'default' && !backgroundColor && style?.backgroundColor == null && !ignoreCurrentBG) {
            append.backgroundColor = 'transparent';
        }

        append.opacity = disabled ? .5 : 1;

        setLocalStyle((prev) =>
            StyleSheet.flatten([normalizeStyle(style), prev, attr, append]) || {}
        );
    }, [options, localStyle, style, disabled, backgroundColor, noSwitch]);

    const localOnPressIn = useCallback(() => {
        if (!reduceMotion && !disabled)
            updateStyles({ opacity: 0.5 });

        if (typeof onPressIn === 'function')
            onPressIn();
    }, [reduceMotion, onPressIn, disabled]);

    const localOnPressOut = useCallback(() => {
        if (!reduceMotion)
            updateStyles({ opacity: 1 });

        if (typeof onPressOut === 'function')
            onPressOut();
    }, [reduceMotion, onPressOut]);

    const localOnHoverIn = useCallback(() => {

        if (hoverStyle != null) {
            updateStyles(hoverStyle);
            return;
        }

        if (type != 'button' || variant != 'outline')
            return;

        let color = null;

        if (localStyle.backgroundColor !== 'transparent')
            color = localStyle.backgroundColor;

        if (color === null && localStyle.borderColor)
            color = localStyle.borderColor;

        if (color !== null) {
            const rgba = hexToRgbA(color, .1);
            if (rgba) updateStyles({ backgroundColor: rgba }, true);
        }

    }, [localStyle, type, variant, hoverStyle]);

    const localOnHoverOut = useCallback(() => {
        if (hoverStyle != null) {
            updateStyles(style);
            return;
        }
        if (type != 'button' || variant != 'outline')
            return;

        updateStyles({ backgroundColor: 'transparent' }, true);
    }, [type, style, variant, hoverStyle]);

    useEffect(() => {
        if (Array.isArray(style) && style.length > 0) {
            updateStyles(StyleSheet.flatten(style));
        } else if (typeof style === 'object' && Object.keys(style).length > 0) {
            updateStyles(StyleSheet.flatten([style]));
        }
    }, [disabled, backgroundColor]);

    return {
        style: localStyle,
        onPressIn: localOnPressIn,
        onPressOut: localOnPressOut,
        onHoverIn: localOnHoverIn,
        onHoverOut: localOnHoverOut,
    }

}